import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { isEmpty } from "lodash";

export const useAuthSocket = ({
  user,
  isSportsPage,
  onCoinsUpdate,
  onForceLogout,
}) => {
  const [score, setScore] = useState([]);
  const [cricketScore, setCricketScore] = useState([]);

  const scoreSocketRef = useRef(null);
  const amountSocketRef = useRef(null);
  const isManualScoreDisconnectRef = useRef(false);
  const isManualAmountDisconnectRef = useRef(false);
  const scoreReconnectTimerRef = useRef(null);
  const amountReconnectTimerRef = useRef(null);

  const onCoinsUpdateRef = useRef(onCoinsUpdate);
  const onForceLogoutRef = useRef(onForceLogout);
  useEffect(() => { onCoinsUpdateRef.current = onCoinsUpdate; });
  useEffect(() => { onForceLogoutRef.current = onForceLogout; });

  // ─── Score Socket ─────────────────────────────────────────────────────────

  const disconnectScoreSocket = useCallback(() => {
    // ✅ Cancel pending reconnect timer first
    if (scoreReconnectTimerRef.current) {
      clearTimeout(scoreReconnectTimerRef.current);
      scoreReconnectTimerRef.current = null;
    }
    if (scoreSocketRef.current) {
      isManualScoreDisconnectRef.current = true;
      scoreSocketRef.current.removeAllListeners(); // ✅ Strip listeners before disconnect
      scoreSocketRef.current.disconnect();
      scoreSocketRef.current = null;
    }
  }, []);

  const scheduleScoreReconnect = useCallback((socketInstance) => {
    if (scoreReconnectTimerRef.current) return; // ✅ Debounce duplicate reconnects
    scoreReconnectTimerRef.current = setTimeout(() => {
      scoreReconnectTimerRef.current = null;
      // ✅ Only reconnect if this socket is still the active one
      if (scoreSocketRef.current === socketInstance) {
        connectScoreSocket();
      }
    }, 2000);
  }, []);

  const connectScoreSocket = useCallback(() => {
    if (!isSportsPage) return;

    // ✅ Skip if socket is mid-handshake (prevents "closed before established")
    if (
      scoreSocketRef.current &&
      !scoreSocketRef.current.connected &&
      scoreSocketRef.current.io?.readyState === "opening"
    ) {
      return;
    }

    disconnectScoreSocket();
    isManualScoreDisconnectRef.current = false;

    const userId = user?.user?._id ?? 112233;
    const url = isEmpty(user)
      ? `${process.env.REACT_APP_API_SCORE_URL}?user_id=${userId}`
      : `${process.env.REACT_APP_API_SCORE_URL}?token=${userId}&userType=front`;

    const socket = io(url, {
      transports: ["websocket"],
      forceNew: true,
      reconnection: false, // ✅ We manage reconnection manually
    });
    scoreSocketRef.current = socket;

    socket.on("scoreAll", setScore);
    socket.on("scoreCricketAll", setCricketScore);

    socket.on("connect_error", (err) => {
      console.warn("[ScoreSocket] connect_error:", err.message);
      if (scoreSocketRef.current === socket) {
        scheduleScoreReconnect(socket); // ✅ Pass instance for staleness check
      }
    });

    socket.on("error", (err) => {
      console.warn("[ScoreSocket] error:", err);
    });

    socket.on("disconnect", (reason) => {
      console.warn("[ScoreSocket] disconnect reason:", reason);
      if (isManualScoreDisconnectRef.current) {
        isManualScoreDisconnectRef.current = false;
        return;
      }
      if (scoreSocketRef.current === socket) { // ✅ Staleness check
        scheduleScoreReconnect(socket);
      }
    });
  }, [isSportsPage, user]);

  // ─── Amount Socket ────────────────────────────────────────────────────────

  const disconnectAmountSocket = useCallback(() => {
    // ✅ Cancel pending reconnect timer first
    if (amountReconnectTimerRef.current) {
      clearTimeout(amountReconnectTimerRef.current);
      amountReconnectTimerRef.current = null;
    }
    if (amountSocketRef.current) {
      isManualAmountDisconnectRef.current = true;
      amountSocketRef.current.removeAllListeners(); // ✅ Strip listeners before disconnect
      amountSocketRef.current.disconnect();
      amountSocketRef.current = null;
    }
  }, []);

  const scheduleAmountReconnect = useCallback((socketInstance) => {
    if (amountReconnectTimerRef.current) return; // ✅ Debounce duplicate reconnects
    amountReconnectTimerRef.current = setTimeout(() => {
      amountReconnectTimerRef.current = null;
      // ✅ Only reconnect if this socket is still the active one
      if (amountSocketRef.current === socketInstance) {
        connectAmountSocket();
      }
    }, 2000);
  }, []);

  const connectAmountSocket = useCallback(() => {
    if (isEmpty(user)) return;

    // ✅ Skip if socket is mid-handshake (prevents "closed before established")
    if (
      amountSocketRef.current &&
      !amountSocketRef.current.connected &&
      amountSocketRef.current.io?.readyState === "opening"
    ) {
      return;
    }

    disconnectAmountSocket();
    isManualAmountDisconnectRef.current = false;

    const token = localStorage.getItem("token");
    const socket = io(
      `${process.env.REACT_APP_API_BASE_URL}?token=${token}&userType=front`,
      {
        transports: ["websocket"],
        reconnection: false, // ✅ We manage reconnection manually
      }
    );
    amountSocketRef.current = socket;

    socket.emit("getCoins", { user_id: user?.user?._id ?? "" });

    socket.on("listenGetCoin", (message) => {
      onCoinsUpdateRef.current?.({
        exposure: message?.results?.exposure,
        totalCoins: message?.results?.totalCoins,
      });
    });

    socket.on("listenForceLogout", (message) => {
      const storedUniqueId = localStorage.getItem("uniqueId");
      if (storedUniqueId !== message?.results?.uniqueId) {
        onForceLogoutRef.current?.();
      }
    });

    socket.on("connect_error", (err) => {
      console.warn("[AmountSocket] connect_error:", err.message);
      if (amountSocketRef.current === socket) {
        scheduleAmountReconnect(socket); // ✅ Pass instance for staleness check
      }
    });

    socket.on("error", (err) => {
      console.warn("[AmountSocket] error:", err);
    });

    socket.on("disconnect", (reason) => {
      console.warn("[AmountSocket] disconnect reason:", reason);
      if (isManualAmountDisconnectRef.current) {
        isManualAmountDisconnectRef.current = false;
        return;
      }
      if (amountSocketRef.current === socket) { // ✅ Staleness check
        scheduleAmountReconnect(socket);
      }
    });
  }, [user]);

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSportsPage) {
      connectScoreSocket();
    } else {
      disconnectScoreSocket();
    }
    return disconnectScoreSocket;
  }, [isSportsPage, user]);

  useEffect(() => {
    if (!isEmpty(user)) {
      connectAmountSocket();
    } else {
      disconnectAmountSocket();
    }
    return disconnectAmountSocket;
  }, [user]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        disconnectScoreSocket();
        disconnectAmountSocket();
      } else {
        if (isSportsPage) connectScoreSocket();
        if (!isEmpty(user)) connectAmountSocket();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isSportsPage, user]);

  return {
    score,
    cricketScore,
    reconnectAmountSocket: connectAmountSocket,
    disconnectAmountSocket,
  };
};